import React, { useCallback, useContext, useEffect, useRef } from 'react'
import styled from 'styled-components'
import cn from 'clsx'
import Caret from 'components/Common/Caret'
import Clickable from 'components/Common/Clickable'
import { rem } from 'polished'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { getDurationOptions, isAvailableForPurchase } from 'selectors/offerPage/offerPageSelectors'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import BodyText from 'LuxKit/Typography/BodyText'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import { packageOptionsClick } from 'analytics/packageDetails'
import { addGTMEvent } from 'api/googleTagManager'
import { mediaQueryUp } from 'components/utils/breakpoint'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import { isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import Group from 'components/utils/Group'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { INCLUSIONS_TITLE_BONUS } from 'constants/inclusions'
import Plural from 'components/utils/Formatters/Plural'
import { OFFER_TYPE_HOTEL } from 'constants/offer'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'

export interface OfferDurationOption {
  lowestPrice?: number
  lowestMemberPrice?: number
  soldOut?: boolean
  duration: number
  hasBonusInclusions?: boolean
  hasTactical?: boolean
  discountPercent?: number
  stayPayTier?: App.StayPayTier
}

const DurationFromText = styled(BodyText)`
  transition: color 0.2s;
`

const DurationCaret = styled(Caret)`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateY(100%) translateX(-50%);
  opacity: 0;
  transition: opactiy 0.2s, border-color 0.2s;
  border-top-color: ${props => props.theme.palette.brand.primary.normal};
  border-width: ${rem(8)};

  &.visible {
    opacity: 1;
  }
`

const DurationOption = styled(Clickable)`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  transition: color 0.2s, background-color 0.2s;
  box-shadow: ${props => props.theme.shadow.bottom.small};
  position: relative;
  min-width: ${rem(120)};
  align-self: flex-end;
  border: 1px solid ${(props) => props.theme.palette.neutral.default.five};

  &.selected {
    color: ${props => props.theme.palette.neutral.default.eight};
    background-color: ${props => props.theme.palette.brand.primary.normal};

    ${mediaHoverable} {
      &:hover {
        color: ${props => props.theme.palette.neutral.default.eight};
        background-color: ${props => props.theme.palette.brand.primary.hover};

        ${DurationCaret} {
          border-top-color: ${props => props.theme.palette.brand.primary.hover};
        }
      }
    }
    &:focus {
      color: ${props => props.theme.palette.neutral.default.eight};
      background-color: ${props => props.theme.palette.brand.primary.hover};

      ${DurationCaret} {
        border-top-color: ${props => props.theme.palette.brand.primary.hover};
      }
    }
  }

  &:hover, &:focus {
    color: ${props => props.theme.palette.neutral.default.eight};
    background-color: ${props => props.theme.palette.brand.primary.normal};

    ${DurationFromText} {
      color: ${props => props.theme.palette.neutral.default.eight};
    }
  }
`

const DurationOptionContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: ${rem(44)};
  padding: ${rem(4)} ${rem(12)};
`

const BonusInclusionTag = styled(Group)`
  padding: ${rem(4)};
  background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
`

const OfferDurations = styled.div`
  ${mediaQueryUp.desktop} {
    &.centered {
      display: flex;
      justify-content: center;
    }
  }
`

interface Props {
  durationOptions: Array<OfferDurationOption>
  durationOptionsOverride?: Array<OfferDurationOption>
  selectedDuration: number
  onBeforeDurationClick?: (duration: number) => boolean
  className?: string
  offerType?: App.OfferType
  availableForPurchase?: boolean
  availableForPurchaseOverride?: boolean
}

function OfferDurationTabs(props: Props) {
  const { className, durationOptions, selectedDuration, onBeforeDurationClick, availableForPurchase } = props
  const tabRefs = useRef<Record<string, HTMLButtonElement>>({})

  const { trackEvent } = useContext(OfferPageStateTrackingContext)
  const offerPageDispatch = useOfferPageDispatch()

  const onDurationClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const duration = parseInt(e.currentTarget.dataset.duration!, 10)
    if (onBeforeDurationClick && !onBeforeDurationClick(duration)) return
    offerPageDispatch({ type: OfferPageActions.setDuration, duration })
    addGTMEvent(packageOptionsClick(duration))
    trackEvent('DURATION_PRESSED')
  }, [offerPageDispatch, onBeforeDurationClick, trackEvent])

  useEffect(() => {
    const tabElement = tabRefs.current[selectedDuration]
    // ensure the selected duration is always visible in the carousel
    if (tabElement?.parentElement) {
      const startElementPx = tabElement.offsetLeft
      const endElementPx = tabElement.offsetLeft + tabElement.offsetWidth
      const startParentPx = tabElement.parentElement.scrollLeft
      const endParentPx = tabElement.parentElement.offsetWidth + tabElement.parentElement.scrollLeft

      // check if our elements bounds are outside of the currently visible bounds on the scroll parent
      const isNotVisible = startElementPx > endParentPx || endElementPx < startParentPx
      if (isNotVisible) {
        tabElement.parentElement.scrollLeft = startElementPx
      }
    }
  }, [selectedDuration])

  return (<OfferDurations className={cn(className, { centered: durationOptions.length < 8 })}>
    <CardCarousel gap={12} pageSize={Math.min(durationOptions.length, 8)}>
      {durationOptions.map((option, index) => {
        const selected = selectedDuration === option.duration
        const hasBonus = option?.hasBonusInclusions
        const hasTactical = isLPCTacticalEnabled() && option?.hasTactical
        const stayPayTier = option?.stayPayTier
        const isFlash = props.offerType === OFFER_TYPE_HOTEL

        return <DurationOption
          key={index}
          className={cn({ selected })}
          data-testid={`nights-${option.duration}`}
          data-duration={option.duration}
          onClick={onDurationClick}
          ref={(ref) => {
            if (ref instanceof HTMLButtonElement) {
              tabRefs.current[option.duration] = ref
            }
          }}
        >
          {!isFlash && (hasBonus || hasTactical) && <BonusInclusionTag direction="horizontal" horizontalAlign="center">
            <BodyTextBlock
              startIcon={hasTactical ? <SolidFireIcon /> : undefined}
              variant="small"
              colour="neutral-one"
              wrap="no-wrap"
              align="center"
              format="propercase"
            >
              {INCLUSIONS_TITLE_BONUS}
            </BodyTextBlock>
          </BonusInclusionTag>}
          <DurationOptionContent>
            <BodyText variant="medium" weight="bold" wrap="no-wrap">
              <Plural singular="night" count={option.duration} withCount />
            </BodyText>
            <BodyText variant="small" weight="bold" colour="highlight-secondary">
              {stayPayTier && <><Plural singular="night" count={stayPayTier.freeNights} withCount /> free</>}
            </BodyText>
            <DurationFromText variant="small" weight="bold" colour={!selected ? 'primary' : undefined}>
              {!availableForPurchase && 'SOLD OUT'}
            </DurationFromText>
            <DurationCaret className={cn({ visible: selected })} />
          </DurationOptionContent>
        </DurationOption>
      })}
    </CardCarousel>
  </OfferDurations>)
}

export default withOfferPageAndReduxState((state, appState, ownProps) => ({
  selectedDuration: state.duration,
  durationOptions: ownProps.durationOptionsOverride ?? getDurationOptions(state, appState),
  offerType: state.offer?.type,
  availableForPurchase: ownProps.availableForPurchaseOverride ?? isAvailableForPurchase(state, appState),
  open: !!state.activeFilter,
}), OfferDurationTabs, preventRenderWithOpenFilter)
